/**
 * Sprint S-2 · #26 · Changelog "¿Qué hay de nuevo?" modal.
 *
 * Lista de versiones con features. Cuando hay una versión más reciente que
 * la última que el usuario vio (guardada en localStorage), muestra una
 * burbuja en la esquina superior derecha del header — al pulsarla, modal.
 *
 * Estático en frontend (no necesita backend). Se actualiza añadiendo entradas
 * al array CHANGELOG abajo.
 */
import { useEffect, useState } from "react";
import { Confetti, X, ArrowSquareOut, Sparkle } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const CHANGELOG = [
  {
    version: "26.06.03",
    date: "3 de junio de 2026",
    highlights: [
      { tag: "IA", text: "El asistente del marketplace ya selecciona servicio Y fecha por el cliente con un solo click." },
      { tag: "Chat", text: "Un solo botón flotante en la ficha pública con opción IA o equipo humano." },
      { tag: "SEO", text: "Nueva landing /centro/<slug> + sitemap dinámico + activación masiva desde SuperAdmin." },
    ],
  },
  {
    version: "26.05.28",
    date: "28 de mayo de 2026",
    highlights: [
      { tag: "Fiscal", text: "Asistente AEAT envía CSV+PDF de cualquier modelo (303, 111, 115, 130, 390) a tu gestor por email." },
      { tag: "Seguridad", text: "Passkeys (Face ID, Touch ID, Windows Hello) operativas con Conditional UI." },
    ],
  },
  {
    version: "26.05.20",
    date: "20 de mayo de 2026",
    highlights: [
      { tag: "Reservas", text: "Validación manual por servicio: marca pintura/peritaje como `pending_review`." },
      { tag: "Marketplace", text: "Filtros por ciudad/localidad/sector + reviews verificadas." },
    ],
  },
];

const LATEST = CHANGELOG[0]?.version;
const LS_KEY = "ag-changelog-seen";

function readSeen() {
  try { return localStorage.getItem(LS_KEY) || ""; } catch { return ""; }
}
function writeSeen(v) {
  try { localStorage.setItem(LS_KEY, v); } catch {}
}

/**
 * Punto de entrada visual: badge "¡Novedades!" + modal.
 * Para integrar en headers/footers/etc.
 */
export function ChangelogButton({ className = "" }) {
  const [open, setOpen] = useState(false);
  const [hasNew, setHasNew] = useState(false);

  useEffect(() => {
    setHasNew(readSeen() !== LATEST);
  }, []);

  const openModal = () => {
    setOpen(true);
    writeSeen(LATEST);
    setHasNew(false);
  };

  return (
    <>
      <button
        onClick={openModal}
        className={`relative inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-[var(--ag-gold)] transition-colors ${className}`}
        data-testid="changelog-button"
        aria-label="Ver novedades"
      >
        <Sparkle size={14} weight={hasNew ? "fill" : "regular"} className={hasNew ? "text-amber-500" : ""} />
        <span className="hidden sm:inline">Novedades</span>
        {hasNew && (
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full ring-2 ring-white animate-pulse" data-testid="changelog-dot" />
        )}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setOpen(false)}
          data-testid="changelog-overlay"
        >
          <div
            className="bg-white text-[#1A1A1A] w-full max-w-lg max-h-[80vh] rounded-md shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="changelog-modal"
          >
            <div className="bg-[#1A1A1A] text-white px-5 py-4 flex items-center justify-between shrink-0">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] opacity-70">AutoGlow Ecosystem</div>
                <div className="font-display font-bold text-lg mt-0.5 flex items-center gap-2">
                  <Confetti size={18} weight="fill" className="text-amber-400" /> ¿Qué hay de nuevo?
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-[var(--ag-text)]/80 hover:text-white p-1"
                aria-label="Cerrar"
                data-testid="changelog-close"
              >
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="overflow-y-auto p-5 space-y-6">
              {CHANGELOG.map((v, idx) => (
                <div key={v.version} className={idx > 0 ? "pt-5 border-t border-gray-200" : ""} data-testid={`changelog-version-${v.version}`}>
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="font-display font-bold text-base">v{v.version}</span>
                    <span className="text-[10px] uppercase tracking-wider text-gray-500">{v.date}</span>
                  </div>
                  <ul className="space-y-2">
                    {v.highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded mt-0.5 shrink-0">{h.tag}</span>
                        <span className="text-gray-700 leading-relaxed">{h.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="border-t border-gray-200 px-5 py-3 flex items-center justify-between bg-gray-50 shrink-0">
              <Link
                to="/ayuda"
                className="text-xs text-[var(--ag-gold)] hover:underline inline-flex items-center gap-1"
                data-testid="changelog-help-link"
                onClick={() => setOpen(false)}
              >
                Centro de ayuda <ArrowSquareOut size={12} weight="bold" />
              </Link>
              <button
                onClick={() => setOpen(false)}
                className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] text-white px-4 py-1.5 rounded text-xs font-semibold"
                data-testid="changelog-dismiss"
              >
                ¡Genial!
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
